import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { formatINR } from "@/lib/format";

const CHANNEL_COLORS = { Mediator: "#1E3A8A", Commission: "#EA580C", Direct: "#16A34A", Repeat: "#7C3AED" };
const shortINR = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 10000000) return "₹" + (v / 10000000).toFixed(1) + "Cr";
  if (Math.abs(v) >= 100000) return "₹" + (v / 100000).toFixed(1) + "L";
  if (Math.abs(v) >= 1000) return "₹" + (v / 1000).toFixed(0) + "k";
  return "₹" + v;
};

const CardShell = ({ title, children }) => (
  <div className="a1-card p-5 sm:p-6">
    <h3 className="font-head font-extrabold text-lg sm:text-xl tracking-tight mb-4">{title}</h3>
    {children}
  </div>
);

const TT = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-lg text-sm">
      {label && <div className="font-bold mb-1">{label}</div>}
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 font-semibold">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-slate-500">{p.name}:</span>
          <span className="num font-bold">{formatINR(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function DashboardCharts({ data }) {
  if (!data) return null;
  const monthly = data.monthly || [];
  const channels = data.channels || [];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6 mb-8">
      <div className="xl:col-span-2">
        <CardShell title="Monthly Sales & Profit">
          <div style={{ width: "100%", height: 260 }} data-testid="chart-sales-profit">
            <ResponsiveContainer>
              <BarChart data={monthly} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#475569", fontWeight: 700, fontSize: 13 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={shortINR} tick={{ fill: "#94A3B8", fontSize: 12 }} axisLine={false} tickLine={false} width={54} />
                <Tooltip content={<TT />} cursor={{ fill: "rgba(30,58,138,0.05)" }} />
                <Legend wrapperStyle={{ fontWeight: 700, fontSize: 13 }} />
                <Bar dataKey="revenue" name="Revenue" fill="#1E3A8A" radius={[6, 6, 0, 0]} maxBarSize={38} />
                <Bar dataKey="profit" name="Net Margin" fill="#EA580C" radius={[6, 6, 0, 0]} maxBarSize={38} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardShell>
      </div>

      <CardShell title="Sourcing Channels">
        <div style={{ width: "100%", height: 260 }} data-testid="chart-channels">
          {channels.length === 0 ? (
            <div className="h-full flex items-center justify-center text-slate-400 font-semibold">No data</div>
          ) : (
            <ResponsiveContainer>
              <PieChart>
                <Pie data={channels} dataKey="value" nameKey="name" innerRadius={58} outerRadius={95} paddingAngle={3} stroke="none">
                  {channels.map((c) => (
                    <Cell key={c.name} fill={CHANNEL_COLORS[c.name] || "#64748B"} />
                  ))}
                </Pie>
                <Tooltip content={({ active, payload }) =>
                  active && payload?.length ? (
                    <div className="bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-lg text-sm font-semibold">
                      {payload[0].name}: <span className="font-bold num">{payload[0].value}</span> cars
                    </div>
                  ) : null
                } />
                <Legend wrapperStyle={{ fontWeight: 700, fontSize: 13 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardShell>

      <div className="xl:col-span-3">
        <CardShell title="Cash vs Bank Inflow (Monthly)">
          <div style={{ width: "100%", height: 240 }} data-testid="chart-inflow">
            <ResponsiveContainer>
              <BarChart data={monthly} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#475569", fontWeight: 700, fontSize: 13 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={shortINR} tick={{ fill: "#94A3B8", fontSize: 12 }} axisLine={false} tickLine={false} width={54} />
                <Tooltip content={<TT />} cursor={{ fill: "rgba(30,58,138,0.05)" }} />
                <Legend wrapperStyle={{ fontWeight: 700, fontSize: 13 }} />
                <Bar dataKey="cash_in" name="Cash In" fill="#16A34A" radius={[6, 6, 0, 0]} maxBarSize={38} />
                <Bar dataKey="bank_in" name="Bank / Online In" fill="#2563EB" radius={[6, 6, 0, 0]} maxBarSize={38} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardShell>
      </div>
    </div>
  );
}
